import { Flex, Progress, Space, List, Modal, Button } from "antd";
import { Alert } from "components/elements";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from 'dayjs';
import * as services from "../services/homeApi";
import { setIsLoading, setRemiainingLeaveList, setAllRemainingLeaveLists } from "../slices/homeSlice";

const summaryLeaveTypeOrder = ["Annual Leave", "Sick Leave", "Business Leave", "Business Leave (without pay)"];

const colors = [
    {
        name: "Business Leave",
        color: "#FF4D26"
    },
    {
        name: "Annual Leave",
        color: "#008EFF"
    },
    {
        name: "Sick Leave",
        color: "#8357DF"
    },
    {
        name: "Maternity Leave",
        color: "#00AE50"
    },
    {
        name: "Business Leave (without pay)",
        color: "#FFB21C"
    },
]

const fallbackColors = [
    "#AEEA94", "#13C2C2", "#2F54EB", "#D4380D", "#389E0D", "#7A73D1",
    "#F759AB", "#FF6326", "#A0D911", "#9254DE", "#F39F5A", "#FADA7A"
];

const assignedColors = new Map();
let fallbackIndex = 0;

const getLeaveTypeColor = (leaveTypeName) => {
    const matchingType = colors.find(item => item.name === leaveTypeName);

    if (matchingType) {
        return matchingType.color;
    }

    if (!assignedColors.has(leaveTypeName)) {
        assignedColors.set(leaveTypeName, fallbackColors[fallbackIndex]);
        fallbackIndex = (fallbackIndex + 1) % fallbackColors.length; // วนไปเรื่อย ๆ
    }

    return assignedColors.get(leaveTypeName);
};

function useNotiTab() {
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const { remiainingLeaveList, allRemainingLeaveLists } = useSelector((state) => state.home);
    const { roles } = useSelector((state) => state.company);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch()

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [allDataFetched, setAllDataFetched] = useState(false);

    const [leaveHistoryForRanking, setLeaveHistoryForRanking] = useState([]);

    const handleCancel = () => setIsModalOpen(false);
    const getLeaveHistoryForRanking = useCallback(async (params) => {
        try {
            const res = await services.getMyLeaveRequestPagingWithoutPending({
                page: 0,
                size: 20, // ขอเยอะพอสำหรับคำนวณ ranking เท่านั้น ไม่เอาไปโชว์ตรงๆ
                companyId: params.currentCompany,
                employeeId: params.currentEmployee,
            });
            setLeaveHistoryForRanking(res?.data?.content ?? []);
        } catch (error) {
            // silent fail — ถ้าพลาด ranking จะ fallback เป็นลำดับเดิมจาก backend
        }
    }, []);
    const getDataRemaining = useCallback(
        async (params) => {
            try {
                dispatch(setIsLoading(true));
                const res = await services.getRemainingLeaveWithIn({
                    companyId: params.currentCompany,
                    employeeId: params.currentEmployee,
                    listName: colors.map(o => o.name)
                })
                dispatch(setRemiainingLeaveList(res.data))
            } catch (error) {
                // Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [colors],
    )

    const getAllDataRemaining = useCallback(
        async (params) => {
            try {
                dispatch(setIsLoading(true));
                const res = await services.getRemainingLeaveWithIn({
                    companyId: params.currentCompany,
                    employeeId: params.currentEmployee,
                    listName: [],
                    type: "leave"
                })
                dispatch(setAllRemainingLeaveLists(res.data))
            } catch (error) {
                // Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch],
    )
    useEffect(() => {
        if (currentEmployee && currentCompany && roles.length != 0) {
            if (roles.includes("ACCESS_HOME")) {
                getDataRemaining({ currentCompany, currentEmployee });
                getAllDataRemaining({ currentCompany, currentEmployee });
                getLeaveHistoryForRanking({ currentCompany, currentEmployee });
            }
        }
    }, [currentCompany, currentEmployee, roles])

    const leaveTypeLastUsedMap = useMemo(() => {
        const map = {};
        (leaveHistoryForRanking ?? []).forEach((req) => {
            const key = req?.leaveTypeNameEn;
            if (!key) return;
            const date = req?.lastModifyDate ?? req?.startDate;
            if (!date) return;
            if (!map[key] || dayjs(date).isAfter(dayjs(map[key]))) {
                map[key] = date;
            }
        });
        return map;
    }, [leaveHistoryForRanking]);

    const showModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);
    const convertLeaveHour = (remainingLeaveHour) => {
        const leaveHour = parseFloat(remainingLeaveHour);

        if (leaveHour !== 0) {
            const decimalPart = leaveHour % 1;
            const convertedMinutes = (decimalPart * 60) / 100;
            const result = Math.floor(leaveHour) + convertedMinutes;

            if (result % 1 === 0) {
                return `${result} ${t("unit.hours")}`;
            } else {
                return `${result.toFixed(2)} ${t("unit.hours")}`;
            }
        }

        return "";
    };

    const ROW_HEIGHT = 30;
    const ROW_GAP = 18;
    const VISIBLE_ROWS = 6;
    const maxListHeight = VISIBLE_ROWS * ROW_HEIGHT + (VISIBLE_ROWS - 1) * ROW_GAP;

    const remainingAllModal = useCallback(() => {
        const scrollable = (allRemainingLeaveLists?.length ?? 0) > VISIBLE_ROWS;
        return (
            <Modal
                title={t('home.remaining-leave-benefits.title')}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={false}
                width={560}
            >
                {scrollable && (
                    <style>{`
                    .leave-breakdown-scroll::-webkit-scrollbar { width: 6px; }
                    .leave-breakdown-scroll::-webkit-scrollbar-track { background: #f0f0f0; border-radius: 10px; }
                    .leave-breakdown-scroll::-webkit-scrollbar-thumb { background: #c0c0c0; border-radius: 10px; }
                    .leave-breakdown-scroll::-webkit-scrollbar-thumb:hover { background: #a0a0a0; }
                `}</style>
                )}
                <div
                    className={scrollable ? 'leave-breakdown-scroll' : ''}
                    style={{
                        maxHeight: scrollable ? maxListHeight : 'none',
                        overflowY: scrollable ? 'auto' : 'visible',
                        paddingRight: scrollable ? 10 : 0,
                        scrollbarWidth: scrollable ? 'thin' : 'auto',
                        scrollbarColor: scrollable ? '#c0c0c0 #f0f0f0' : 'auto',
                    }}
                >
                    <Flex vertical gap={ROW_GAP}>
                        {(allRemainingLeaveLists ?? []).map((item, i) => {
                            const language = i18n.language == 'en' ? 'En' : '';
                            const textName = String(item?.leaveTypeNameEn);
                            const label = item?.['leaveTypeName' + language];
                            const color = getLeaveTypeColor(textName);
                            const isUnlimited = item?.unlimited === 'Y';
                            const percent = isUnlimited
                                ? 100
                                : (item?.totalLeaveDays ? (item?.remainingLeaveDays / item?.totalLeaveDays) * 100 : 0);
                            const valueText = isUnlimited
                                ? t('unlimited')
                                : `${item?.remainingLeaveDays ?? 0} / ${item?.totalLeaveDays ?? 0} ${t('unit.day')}`;

                            return (
                                <Flex key={i} align="center" gap={12}>
                                    <span style={{ width: 14, height: 14, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                    <span style={{ width: 170, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }} title={label}>{label}</span>
                                    <div style={{ flex: '0 1 45%', maxWidth: '45%', background: '#f0f0f0', borderRadius: 20, height: 14, marginLeft: -5 }}>
                                        <div style={{
                                            width: `${percent}%`,
                                            background: color,
                                            height: '100%',
                                            borderRadius: 20,
                                            transition: 'width 0.3s',
                                        }} />
                                    </div>
                                    <span style={{ flex: 1, fontSize: 15, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                        {valueText}
                                    </span>
                                </Flex>
                            );
                        })}
                    </Flex>
                </div>
            </Modal>
        );
    }, [isModalOpen, allRemainingLeaveLists, i18n.language, t]);

    const remaining = useCallback(
        () => {
            return (
                <List
                    grid={{
                        gutter: 0,
                        column: 1
                    }}
                    dataSource={remiainingLeaveList}
                    renderItem={(item, i) => {
                        let textName = String(item?.leaveTypeNameEn);
                        let language = i18n.language == 'en' ? "En" : ""

                        const remainingDay = item?.remainingLeaveDays !== 0 ? `${item?.remainingLeaveDays} ${t("unit.day")}` : ""
                        const remainingHour = item?.remainingLeaveHour !== "0.000000" ? convertLeaveHour(item?.remainingLeaveHour) : ""

                        const totalHours = item?.totalLeaveHour !== "0.000000" ? convertLeaveHour(item?.totalLeaveHour) : ""

                        const remaining = item?.unlimited === 'Y' ? (
                            <span>{t("unlimited")}</span>
                        ) : (
                            <span>{`${remainingDay} ${remainingHour}`} / {`${item?.totalLeaveDays} ${t("unit.day")} ${totalHours}`}</span>
                        );

                        return (
                            <List.Item>
                                <List.Item.Meta
                                    title={
                                        <Flex justify="space-between" align="center">
                                            <span style={{
                                                // fontSize: "0.8vw"
                                            }} color={"green"} placement={"start"} text="">{item?.["leaveTypeName" + language]}
                                            </span>
                                            <span style={{ fontWeight: "normal" }}>{remaining}</span>
                                        </Flex>
                                    }
                                    description={
                                        <Progress
                                            strokeWidth={12} strokeColor={colors.find(o => o.name == (textName))?.color}
                                            percent={item?.unlimited == 'Y' ? 100 : (item?.remainingLeaveDays / item?.totalLeaveDays * 100)}
                                            showInfo={false}
                                        />
                                    }
                                >
                                </List.Item.Meta>
                            </List.Item>
                        )
                    }}
                >
                </List>
            )
        },
        [remiainingLeaveList, colors],
    )

    const getRemaining = useCallback(
        () => {
            return (
                <Flex vertical gap={24}>
                    {remainingAllModal()}
                    {remaining()}
                    <Flex justify="center">
                        <Button size="small" type="text" onClick={showModal}>
                            <span style={{ color: "#009DE6", cursor: "pointer" }}>{t('home.btn.view-all')}</span>
                        </Button>
                    </Flex>
                </Flex>)
        },
        [remaining, remainingAllModal, t],
    )
    const summaryLeaveTypeOrder = ["Annual Leave", "Sick Leave", "Business Leave", "Business Leave (without pay)"];


    const LEAVE_NAME_COL_WIDTH = 180;

    const getLeaveRightsSummary = useCallback(() => {
        const list = remiainingLeaveList ?? [];

        const used = list
            .filter(item => leaveTypeLastUsedMap[item?.leaveTypeNameEn])
            .sort((a, b) =>
                dayjs(leaveTypeLastUsedMap[b.leaveTypeNameEn]).valueOf() -
                dayjs(leaveTypeLastUsedMap[a.leaveTypeNameEn]).valueOf()
            );

        const usedKeys = new Set(used.map(item => item?.leaveTypeNameEn));
        const unused = list.filter(item => !usedKeys.has(item?.leaveTypeNameEn));

        const summaryList = [...used, ...unused].slice(0, 4);

        return (
            <Flex vertical style={{ height: "100%", width: "100%" }}>
                {remainingAllModal()}
                <List
                    size="small"
                    style={{ width: "100%" }}
                    dataSource={summaryList}
                    renderItem={(item, index) => {
                        const language = i18n.language == 'en' ? "En" : "";
                        const textName = String(item?.leaveTypeNameEn);
                        const color = getLeaveTypeColor(textName);
                        const isUnlimited = item?.unlimited === 'Y';
                        const percent = isUnlimited ? 100 : (item?.remainingLeaveDays / item?.totalLeaveDays * 100);
                        const valueText = isUnlimited
                            ? t("unlimited")
                            : `${item?.remainingLeaveDays} / ${item?.totalLeaveDays} ${t("unit.day")}`;
                        const isLast = index === summaryList.length - 1;

                        return (
                            <List.Item
                                style={{
                                    padding: "16px 0",
                                    width: "100%",
                                    borderBottom: isLast ? "none" : "1px solid #F0F0F0",
                                }}
                            >
                                <Flex align="center" gap={10} style={{ width: "100%", fontSize: 14 }}>
                                    <Flex
                                        align="center"
                                        gap={6}
                                        style={{
                                            flex: `0 0 ${LEAVE_NAME_COL_WIDTH}px`,
                                            width: LEAVE_NAME_COL_WIDTH,
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                        <span>{item?.["leaveTypeName" + language]}</span>
                                    </Flex>
                                    <div style={{ flex: "1 1 auto", minWidth: 20, background: '#f0f0f0', borderRadius: 20, height: 8 }}>
                                        <div style={{
                                            width: `${percent}%`,
                                            background: color,
                                            height: '100%',
                                            borderRadius: 20,
                                            transition: 'width 0.3s',
                                        }} />
                                    </div>
                                    <span style={{ flex: "0 0 auto", whiteSpace: 'nowrap', color: '#667085' }}>
                                        {valueText}
                                    </span>
                                </Flex>
                            </List.Item>
                        );
                    }}
                />
                <button
                    onClick={showModal}
                    style={{
                        marginTop: "auto",
                        width: "100%",
                        padding: "8px 0",
                        backgroundColor: "#F5F8FF",
                        color: "#3B82F6",
                        fontWeight: 600,
                        fontSize: 13,
                        border: "1px solid #D6E4FF",
                        borderRadius: 8,
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#E8EFFF"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#F5F8FF"}
                >
                    {t('home.btn.view-all')}
                </button>
            </Flex>
        );
    }, [remiainingLeaveList, leaveTypeLastUsedMap, i18n.language, t, remainingAllModal, showModal]);
    return {
        getRemaining,
        getLeaveRightsSummary
    };
}
export default useNotiTab;